import { useEffect, useState } from 'react';
import { saxoApi, type SaxoHealth, type SaxoSnapshot } from '../lib/data.js';
import { PageHeader, Card, StatCard, Button, EmptyState, type Tone } from '../components/ui.js';

/** Format a Saxo money value (major units in its own currency — NOT app øre). */
function fmtSaxoMoney(amount: number | undefined, currency: string | undefined): string {
  if (amount === undefined) return '—';
  try {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: currency || 'DKK',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount).toLocaleString('da-DK')} ${currency ?? ''}`.trim();
  }
}

/** Absolute → friendly relative-ish timestamp (local). */
function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('da-DK', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Map health → a headline label + tone for the status stat. */
function statusView(h: SaxoHealth | null): { label: string; tone: Tone; hint: string } {
  if (!h || !h.connected) {
    return { label: 'Not connected', tone: 'default', hint: 'Link your Saxo account to see live holdings.' };
  }
  if (h.status === 'stale') {
    return { label: 'Needs re-auth', tone: 'warning', hint: 'The session lapsed — reconnect to resume live reads.' };
  }
  if (h.status === 'revoked') {
    return { label: 'Revoked', tone: 'warning', hint: 'Access was revoked — reconnect to restore.' };
  }
  return { label: 'Connected', tone: 'success', hint: 'Server keeps the session alive automatically.' };
}

export function Saxo(): JSX.Element {
  const [health, setHealth] = useState<SaxoHealth | null>(null);
  const [snapshot, setSnapshot] = useState<SaxoSnapshot | null>(null);
  const [busy, setBusy] = useState<null | 'connect' | 'refresh' | 'holdings'>(null);
  const [error, setError] = useState<string | null>(null);
  const [justConnected, setJustConnected] = useState(false);

  const loadStatus = () =>
    saxoApi
      .status()
      .then(setHealth)
      .catch(() => setError('Could not read Saxo status.'));

  // On mount: consume the ?saxo=connected marker from the callback redirect, then read status.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('saxo') === 'connected') {
      setJustConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
    void loadStatus();
  }, []);

  // Once connected, pull a holdings snapshot for the summary.
  useEffect(() => {
    if (!health?.connected || health.status === 'revoked') return;
    setBusy('holdings');
    saxoApi
      .holdings()
      .then(setSnapshot)
      .catch(() => {
        /* 409/424 handled via the status card; leave the summary empty */
      })
      .finally(() => setBusy(null));
  }, [health?.connected, health?.status, health?.lastRefreshAt]);

  const connect = async (): Promise<void> => {
    setBusy('connect');
    setError(null);
    try {
      const { authorizeUrl } = await saxoApi.connect();
      // Leave the SPA for the Saxo login; Saxo redirects back to /api/saxo/callback → /?saxo=connected.
      window.location.href = authorizeUrl;
    } catch {
      setBusy(null);
      setError('Could not start the Saxo connection. Try again.');
    }
  };

  const refresh = async (): Promise<void> => {
    setBusy('refresh');
    setError(null);
    try {
      setHealth(await saxoApi.refresh());
    } catch {
      setError('Refresh failed — you may need to reconnect.');
      void loadStatus();
    } finally {
      setBusy(null);
    }
  };

  const sv = statusView(health);
  const connected = Boolean(health?.connected) && health?.status !== 'revoked';

  return (
    <div>
      <PageHeader
        title="Saxo Bank"
        subtitle="Read-only link to your Saxo account — live balances, positions and exposure. Heivol never trades or moves money."
      />

      {justConnected ? (
        <div className="mb-4 rounded-hv-md border border-hv-success bg-hv-success-surface px-4 py-2 text-hv-sm text-hv-success-text">
          Saxo connected. Your session will now stay alive automatically.
        </div>
      ) : null}
      {error ? (
        <div className="mb-4 rounded-hv-md border border-hv-warning bg-hv-warning-surface px-4 py-2 text-hv-sm text-hv-warning-text">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Connection" value={sv.label} hint={sv.hint} tone={sv.tone} />
        <StatCard label="Last refresh" value={fmtWhen(health?.lastRefreshAt ?? null)} />
        <StatCard
          label="Session valid until"
          value={fmtWhen(health?.refreshExpiresAt ?? null)}
          hint={connected ? 'Auto-renewed every ~15 min' : undefined}
        />
        <StatCard
          label="Portfolio value"
          value={
            snapshot ? fmtSaxoMoney(snapshot.balances?.TotalValue, snapshot.balances?.Currency) : '—'
          }
          hint={snapshot ? `${snapshot.positions.length} positions · ${snapshot.accounts.length} accounts` : undefined}
        />
      </div>

      <Card className="mt-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {connected ? (
            <>
              <Button onClick={() => void refresh()} disabled={busy !== null} variant="secondary">
                {busy === 'refresh' ? 'Refreshing…' : 'Refresh now'}
              </Button>
              <Button onClick={() => void connect()} disabled={busy !== null} variant="ghost">
                Reconnect
              </Button>
              <span className="text-hv-xs text-hv-text-tertiary">
                Reconnect only if reads start failing — the server keeps the session alive on its own.
              </span>
            </>
          ) : (
            <>
              <Button onClick={() => void connect()} disabled={busy !== null}>
                {busy === 'connect' ? 'Opening Saxo…' : 'Connect Saxo'}
              </Button>
              <span className="text-hv-xs text-hv-text-tertiary">
                You'll log in at Saxo — Heivol never sees your Saxo password.
              </span>
            </>
          )}
        </div>
      </Card>

      {connected && snapshot ? (
        <Card className="mt-4 p-4">
          <h2 className="mb-3 text-hv-sm font-medium text-hv-text">Holdings summary</h2>
          <div className="grid gap-3 text-hv-sm sm:grid-cols-3">
            <div>
              <div className="text-hv-xs uppercase tracking-wide text-hv-text-tertiary">Cash</div>
              <div className="mt-0.5 tabular-nums">
                {fmtSaxoMoney(snapshot.balances?.CashBalance, snapshot.balances?.Currency)}
              </div>
            </div>
            <div>
              <div className="text-hv-xs uppercase tracking-wide text-hv-text-tertiary">Net positions</div>
              <div className="mt-0.5 tabular-nums">{snapshot.netPositions.length}</div>
            </div>
            <div>
              <div className="text-hv-xs uppercase tracking-wide text-hv-text-tertiary">FX exposures</div>
              <div className="mt-0.5 tabular-nums">{snapshot.exposure.fxSpot.length}</div>
            </div>
          </div>
          <div className="mt-3 text-hv-xs text-hv-text-tertiary">
            As of {fmtWhen(snapshot.fetchedAt)} · cached ~60s
          </div>
        </Card>
      ) : connected && busy === 'holdings' ? (
        <Card className="mt-4 p-4 text-hv-sm text-hv-text-secondary">Loading holdings…</Card>
      ) : !connected ? (
        <EmptyState>
          Connect your Saxo account to see live balances, positions and exposure here.
        </EmptyState>
      ) : null}
    </div>
  );
}
